/**
 * Phase 32 — Manager-side payment-approvals queue.
 *
 * Reachable at /approvals; also surfaced from the booking-detail panel
 * for individual rows. The list is mobile-responsive (table on desktop,
 * stacked cards on small viewports — same pattern as Jobs.jsx).
 *
 * Live-ish refresh: refetches the list every 30s while the page is
 * mounted, plus on window focus.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Loader2, HandCoins, Clock, User } from "lucide-react";

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    const h = Math.floor(diffMin / 60);
    return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
  } catch {
    return "—";
  }
}

function RejectDialog({ approval, open, onOpenChange, onRejected }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please give a reason — the staff will see this.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/payment-approvals/${approval.id}/reject`,
        { rejection_reason: reason.trim() },
      );
      toast.success("Rejected — staff will be notified");
      onRejected?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not reject");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-approval-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject payment mark</DialogTitle>
          <DialogDescription>
            Tell {approval?.marked_by_staff_name || "the staff"} what's missing —
            they'll see this on their job card.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">
            Reason
          </Label>
          <Textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. cash short by €20, please recount and resubmit"
            data-testid="reject-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm rejection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalRow({ approval, onApproved, onRejected, currency }) {
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/payment-approvals/${approval.id}/approve`);
      toast.success("Approved — payment marked paid");
      onApproved?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not approve");
    } finally {
      setApproving(false);
    }
  };
  const amountStr = approval.amount != null
    ? formatCurrency(Number(approval.amount), currency || "EUR")
    : "—";
  const isPending = approval.decision == null;
  return (
    <div
      className="border border-border rounded-[8px] p-4 bg-card flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
      data-testid={`approval-row-${approval.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {approval.booking_code}
          </span>
          <span className="font-semibold truncate">{approval.customer_name || "—"}</span>
          {!isPending && (
            <span
              className={`text-[10px] mono uppercase tracking-wider rounded-full px-2 py-0.5 ${
                approval.decision === "approved"
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                  : "bg-rose-100 text-rose-800 border border-rose-300"
              }`}
              data-testid={`approval-decision-${approval.id}`}
            >
              {approval.decision}
            </span>
          )}
        </div>
        <div className="mt-1.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {approval.marked_by_staff_name || "—"}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtRelative(approval.marked_at)}</span>
          <span className="inline-flex items-center gap-1 mono uppercase text-xs"><HandCoins className="h-3.5 w-3.5" /> {approval.method}</span>
          <span className="font-bold text-foreground mono">{amountStr}</span>
        </div>
        {approval.note && (
          <div className="mt-1 text-xs text-muted-foreground italic">
            "{approval.note}"
          </div>
        )}
        {approval.rejection_reason && (
          <div className="mt-1.5 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1">
            Rejected: {approval.rejection_reason}
          </div>
        )}
      </div>
      {isPending && (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={approve}
            disabled={approving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 gap-1"
            data-testid={`approve-btn-${approval.id}`}
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Approve
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectOpen(true)}
            className="border-rose-300 text-rose-700 hover:bg-rose-50 h-9 gap-1"
            data-testid={`reject-btn-${approval.id}`}
          >
            <X className="h-3.5 w-3.5" /> Reject
          </Button>
        </div>
      )}
      <RejectDialog
        approval={approval}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onRejected={onRejected}
      />
    </div>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("EUR");

  const load = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payment-approvals?status=${status}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load approvals");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(tab); }, [load, tab]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setCurrency(data?.currency || "EUR")).catch(() => {});
  }, []);
  // Live-ish: refetch every 30s plus on focus. Keeps the queue fresh
  // without imposing real-time websockets on the manager.
  useEffect(() => {
    const t = setInterval(() => load(tab), 30000);
    const onFocus = () => load(tab);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load, tab]);

  const pendingCount = useMemo(
    () => tab === "pending" ? rows.length : null,
    [tab, rows],
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <header className="flex items-center justify-between mb-4 flex-wrap gap-2" data-testid="approvals-header">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Approvals</h1>
          <p className="text-sm text-muted-foreground">
            Staff-marked payments waiting for your confirmation.
          </p>
        </div>
        {pendingCount != null && pendingCount > 0 && (
          <span className="mono uppercase tracking-wider text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-3 py-1" data-testid="approvals-pending-count">
            {pendingCount} pending
          </span>
        )}
      </header>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="pending" data-testid="approvals-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="approvals-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approvals-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="approvals-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          {loading ? (
            <div className="text-muted-foreground text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground italic bg-secondary/30 border border-dashed border-border rounded-[6px] px-4 py-8 text-center" data-testid="approvals-empty">
              {tab === "pending" ? "Nothing waiting on you right now. ✓" : "No records."}
            </div>
          ) : (
            <div className="space-y-3" data-testid="approvals-list">
              {rows.map((a) => (
                <ApprovalRow
                  key={a.id}
                  approval={a}
                  currency={currency}
                  onApproved={() => load(tab)}
                  onRejected={() => load(tab)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
